import { useState } from "react";
import { useTranslation } from "react-i18next";
import { 
  LayoutDashboard, 
  Users, 
  FolderKanban, 
  MessageSquare, 
  BarChart3, 
  Settings, 
  Sparkles,
  Send,
  FlaskConical,
  MailOpen,
  Megaphone,
  GraduationCap,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

// Import marketing pages
import MarketingDashboard from "./MarketingDashboard";
import Campaigns from "./Campaigns";
import BroadcastPage from "./BroadcastPage";
import MarketingContacts from "./MarketingContacts";
import MarketingGroups from "./MarketingGroups";
import Messages from "./Messages";
import Polls from "./Polls";
import ABTesting from "./ABTesting";
import Tracking from "./Tracking";
import AIAssistant from "./AIAssistant";
import Templates from "./Templates";
import TutorialsPage from "./TutorialsPage";
import FAQPage from "./FAQPage";
import MarketingSettings from "./MarketingSettings";

// Get navigation items based on language
const getNavItems = (isRTL) => [
  { id: "dashboard", icon: LayoutDashboard, label: isRTL ? "لوحة التحكم" : "Dashboard" },
  { id: "campaigns", icon: Send, label: isRTL ? "الحملات" : "Campaigns" },
  { id: "broadcast", icon: Megaphone, label: isRTL ? "البث المباشر" : "Broadcast" },
  { id: "contacts", icon: Users, label: isRTL ? "جهات الاتصال" : "Contacts" },
  { id: "groups", icon: FolderKanban, label: isRTL ? "المجموعات" : "Groups" },
  { id: "messages", icon: MessageSquare, label: isRTL ? "الرسائل" : "Messages" },
  { id: "polls", icon: BarChart3, label: isRTL ? "الاستطلاعات" : "Polls" },
  { id: "ab-testing", icon: FlaskConical, label: isRTL ? "اختبار A/B" : "A/B Testing" },
  { id: "tracking", icon: MailOpen, label: isRTL ? "تتبع البريد" : "Email Tracking" },
  { id: "templates", icon: FileText, label: isRTL ? "القوالب الجاهزة" : "Templates" },
  { id: "ai", icon: Sparkles, label: isRTL ? "مساعد AI" : "AI Assistant" },
  { id: "tutorials", icon: GraduationCap, label: isRTL ? "تعليمي" : "Tutorials" },
  { id: "faq", icon: HelpCircle, label: isRTL ? "سؤال وجواب" : "FAQ" },
  { id: "settings", icon: Settings, label: isRTL ? "الإعدادات" : "Settings" },
];

// Component mapping
const componentMap = {
  dashboard: MarketingDashboard,
  campaigns: Campaigns,
  broadcast: BroadcastPage,
  contacts: MarketingContacts,
  groups: MarketingGroups,
  messages: Messages,
  polls: Polls,
  "ab-testing": ABTesting,
  tracking: Tracking,
  templates: Templates,
  ai: AIAssistant,
  tutorials: TutorialsPage,
  faq: FAQPage,
  settings: MarketingSettings,
};

export default function Marketing() {
  const [activePage, setActivePage] = useState("dashboard");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { isRTL } = useLanguage();
  const { t } = useTranslation();
  
  const marketingNavItems = getNavItems(isRTL);
  const ActiveComponent = componentMap[activePage] || MarketingDashboard;

  // Navigate function to pass to child components
  const navigateTo = (pageId) => {
    setActivePage(pageId);
  };

  return (
    <div className="flex h-[calc(100vh-80px)] -m-4 lg:-m-6" data-testid="marketing-page">
      {/* Marketing Sidebar */}
      <aside 
        className={`
          bg-gradient-to-b from-slate-900/90 to-slate-950/95 border-l border-emerald-500/20 flex flex-col transition-all duration-300 backdrop-blur-xl
          ${sidebarCollapsed ? 'w-16' : 'w-60'}
        `}
      >
        {/* Header */}
        <div className="p-4 border-b border-emerald-500/20">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                  <Send size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-white">Mega-Reachy</h2>
                  <p className="text-xs text-emerald-400/70">{isRTL ? 'منصة التسويق المتكاملة' : 'Marketing Platform'}</p>
                </div>
              </div>
            )}
            <Button 
              variant="ghost" 
              size="icon"
              className="h-8 w-8 text-neutral-400 hover:text-white hover:bg-white/10"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              {sidebarCollapsed ? (isRTL ? <ChevronRight size={16} /> : <ChevronLeft size={16} />) : (isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />)}
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto scrollbar-thin">
          {marketingNavItems.map((item, index) => {
            const isActive = activePage === item.id;
            const Icon = item.icon;
            
            // Add separator before AI section
            const showSeparator = item.id === "ai";
            
            return (
              <div key={item.id}>
                {showSeparator && (
                  <div className="my-3 border-t border-white/10" />
                )}
                <button
                  onClick={() => setActivePage(item.id)}
                  data-testid={`marketing-nav-${item.id}`}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
                    ${isActive 
                      ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-400 shadow-lg shadow-emerald-500/10 border border-emerald-500/30' 
                      : 'text-neutral-400 hover:bg-white/5 hover:text-white'
                    }
                    ${sidebarCollapsed ? 'justify-center px-2' : ''}
                  `}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <Icon size={18} className={isActive ? 'text-emerald-400' : ''} />
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </button>
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        {!sidebarCollapsed && (
          <div className="p-4 border-t border-white/10">
            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-xl p-3 border border-emerald-500/20">
              <p className="text-xs text-emerald-400 font-medium">💡 {t('marketing.tipOfTheDay')}</p>
              <p className="text-xs text-neutral-400 mt-1">
                {t('marketing.tipContent')}
              </p>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="p-6">
          <ActiveComponent onNavigate={navigateTo} />
        </div>
      </main>
    </div>
  );
}
